/**
 * Groups a flattened property bag ("Pset_WallCommon.IsExternal") for the panel:
 * a General group (GlobalId, IfcClass, Name and keys without a dot) first, then
 * one group per Pset prefix, alphabetical.
 */
export type PropertyRow = { key: string; label: string; value: string };
export type PropertyGroup = { name: string; rows: PropertyRow[] };

export const GENERAL_GROUP = 'General';
const SUMMARY_ORDER = ['Name', 'IfcClass', 'GlobalId'];

export function groupProperties(properties: Record<string, string>): PropertyGroup[] {
	const groups = new Map<string, PropertyGroup>();
	const push = (name: string, row: PropertyRow) => {
		let group = groups.get(name);
		if (!group) {
			group = { name, rows: [] };
			groups.set(name, group);
		}
		group.rows.push(row);
	};

	for (const key of SUMMARY_ORDER) {
		if (key in properties) push(GENERAL_GROUP, { key, label: key, value: properties[key] });
	}
	for (const [key, value] of Object.entries(properties)) {
		if (SUMMARY_ORDER.includes(key)) continue;
		const dot = key.indexOf('.');
		if (dot <= 0) push(GENERAL_GROUP, { key, label: key, value });
		else push(key.slice(0, dot), { key, label: key.slice(dot + 1), value });
	}

	const general = groups.get(GENERAL_GROUP);
	const psets = [...groups.values()]
		.filter((g) => g.name !== GENERAL_GROUP)
		.sort((a, b) => a.name.localeCompare(b.name));
	return general ? [general, ...psets] : psets;
}

export function filterGroups(groups: PropertyGroup[], query: string): PropertyGroup[] {
	const q = query.trim().toLowerCase();
	if (!q) return groups;
	return groups
		.map((group) => {
			const groupMatches = group.name.toLowerCase().includes(q);
			const rows = groupMatches
				? group.rows
				: group.rows.filter(
						(r) => r.label.toLowerCase().includes(q) || r.value.toLowerCase().includes(q)
					);
			return { ...group, rows };
		})
		.filter((group) => group.rows.length > 0);
}
